import React, { useState, useEffect } from 'react';
import API from '../../../services/api';
import { API_BASE_URL } from '../../../constants/config';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const getServerRootUrl = () => API_BASE_URL.replace(/\/api\/?$/, '');

const InvoiceGenerate = ({ companies }) => {
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedBike, setSelectedBike] = useState(null);
    
    // Manual Buyer Details States
    const [buyerName, setBuyerName] = useState('');
    const [buyerCnic, setBuyerCnic] = useState('');
    
    // Invoice Financial Details
    const [totalValue, setTotalValue] = useState('');
    const [taxPercent, setTaxPercent] = useState(18);

    const selectedCompany = companies ? companies.find(c => c.id === parseInt(selectedCompanyId)) : null;
    const serverRoot = getServerRootUrl();

    // Auto CNIC Dash Formatting (32203-5843348-9)
    const handleCnicChange = (e) => {
        let val = e.target.value.replace(/\D/g, ''); 
        if (val.length > 13) val = val.substring(0, 13);
        
        let formatted = val;
        if (val.length > 5 && val.length <= 12) {
            formatted = `${val.slice(0, 5)}-${val.slice(5)}`;
        } else if (val.length > 12) {
            formatted = `${val.slice(0, 5)}-${val.slice(5, 12)}-${val.slice(12)}`;
        }
        setBuyerCnic(formatted);
    };

    // Live search bike
    const handleSearch = async (val) => {
        setSearchQuery(val);
        if (val.length < 2) {
            setSearchResults([]);
            return;
        }
        try {
            const res = await API.get(`/v1/others/bikes/search?query=${val}`);
            if (res.data.success) {
                setSearchResults(res.data.data);
            }
        } catch (err) {
            console.error('Search error', err);
        }
    };

    const selectBike = (bike) => {
        setSelectedBike(bike);
        setSearchQuery(`Eng: ${bike.engine_number} | Chassis: ${bike.chassis_number}`);
        setSearchResults([]);
    };

    const valueIncSt = parseFloat(totalValue) || 0;
    const rateSt = parseFloat(taxPercent) / 100;
    const valueExSt = valueIncSt / (1 + rateSt);
    const salesTaxAmount = valueIncSt - valueExSt;

    // Helper function to load image into Base64 for jsPDF
    const loadImageAsBase64 = (url) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = 'Anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL('image/png'));
            };
            img.onerror = () => resolve(null);
            img.src = url;
        });
    };

    // Save to Database
    const handleSaveInvoice = async () => {
        if (!selectedCompany || !selectedBike) {
            alert('Please select both Company and Bike!');
            return;
        }
        try {
            const payload = {
                company_id: selectedCompany.id,
                bike_master_id: selectedBike.id,
                buyer_name: buyerName,
                cnic: buyerCnic,
                sale_tax_percent: taxPercent,
                value_ex_st: valueExSt.toFixed(2),
                sales_tax_amount: salesTaxAmount.toFixed(2),
                total_value_inc_st: valueIncSt.toFixed(2),
                invoice_date: new Date().toISOString().split('T')[0]
            };
            const res = await API.post('/v1/others/invoices', payload);
            if (res.data.success) {
                alert(`Invoice ${res.data.invoice_no || 'Saved'} Successfully to Database!`);
            }
        } catch (err) {
            alert('Failed to save invoice');
        }
    };

    // Download PDF matching exact Excel Format (ST-Memon style)
    const downloadPDF = async () => {
        if (!selectedCompany || !selectedBike) {
            alert('Select company and bike first!');
            return;
        }

        const doc = new jsPDF();
        const formattedDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
        
        let startY = 15;

        // Header Logo Handling
        if (selectedCompany.header_logo) {
            const logoUrl = `${serverRoot}/uploads/${selectedCompany.header_logo}`;
            const imgData = await loadImageAsBase64(logoUrl);
            if (imgData) {
                doc.addImage(imgData, 'PNG', 15, startY, 180, 22);
                startY += 26;
            }
        }

        // Title
        doc.setFontSize(14);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(15, 23, 42);
        doc.text('SALES TAX INVOICE', 105, startY, { align: 'center' });
        
        // Date & Inv No
        doc.setFontSize(9);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(50, 50, 50);
        doc.text(`Date: ${formattedDate}`, 15, startY + 8);
        doc.text(`Inv No: STI-${Math.floor(1000 + Math.random() * 9000)}`, 195, startY + 8, { align: 'right' });

        startY += 16;

        // Seller Information Box (Exact Company Info with Fallbacks)
        const compNtn = selectedCompany.ntn || selectedCompany.NTN || selectedCompany.stn || 'N/A';
        const compStrn = selectedCompany.strn || selectedCompany.STRN || 'N/A';

        doc.setFillColor(248, 250, 252);
        doc.rect(14, startY, 182, 26, 'F');
        doc.setDrawColor(226, 232, 240);
        doc.rect(14, startY, 182, 26, 'S');

        doc.setFontSize(9.5);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(15, 23, 42);
        doc.text(`Name:`, 18, startY + 6);
        doc.text(`${selectedCompany.name}`, 45, startY + 6);

        doc.text(`NTN No:`, 18, startY + 12);
        doc.text(`${compNtn}`, 45, startY + 12);

        doc.text(`STRN:`, 100, startY + 12);
        doc.text(`${compStrn}`, 120, startY + 12);

        doc.text(`Address:`, 18, startY + 19);
        doc.text(`${selectedCompany.address || 'N/A'}`, 45, startY + 19, { maxWidth: 145 });

        startY += 32;

        // Buyer Information Box (Manual Entry)
        doc.setFillColor(255, 255, 255);
        doc.rect(14, startY, 182, 16, 'S');
        doc.setFont("helvetica", "bold");
        doc.text(`Buyer Name:`, 18, startY + 6.5);
        doc.text(`${buyerName || 'N/A'}`, 45, startY + 6.5);

        doc.text(`CNIC:`, 18, startY + 12.5);
        doc.text(`${buyerCnic || 'N/A'}`, 45, startY + 12.5);

        startY += 22;

        // --- EXCEL STYLE DETAILED TABLE ---
        const startX = 14;
        const colWidths = [14, 78, 22, 22, 22, 24]; // Total 182
        const headerHeight = 9;
        const rowHeight = 34; 

        // Header
        doc.setFillColor(15, 23, 42);
        doc.rect(startX, startY, 182, headerHeight, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(8.5);

        const headers = ['Qty', 'Description', 'Value Ex ST', 'Rate of ST', 'Sales Tax', 'Value Inc ST'];
        let curX = startX;
        headers.forEach((h, i) => {
            doc.text(h, curX + (colWidths[i] / 2), startY + 6, { align: 'center' });
            curX += colWidths[i];
        });

        // Body Box
        startY += headerHeight;
        doc.setDrawColor(203, 213, 225);
        doc.rect(startX, startY, 182, rowHeight, 'S');

        // Column Dividers
        curX = startX;
        for (let i = 0; i < colWidths.length - 1; i++) {
            curX += colWidths[i];
            doc.line(curX, startY, curX, startY + rowHeight);
        }

        // Content
        doc.setTextColor(15, 23, 42);
        doc.setFont("helvetica", "normal");
        
        // Qty
        doc.text('1', startX + (colWidths[0] / 2), startY + 17, { align: 'center' });

        // Description Details
        let descX = startX + colWidths[0] + 4;
        doc.setFont("helvetica", "bold");
        doc.text(`Make:`, descX, startY + 7.5);
        doc.text(`Model:`, descX, startY + 14.5);
        doc.text(`Engine No:`, descX, startY + 21.5);
        doc.text(`Chassis No:`, descX, startY + 28.5);

        doc.setFont("helvetica", "normal");
        doc.text(`${selectedBike.model_name || 'CHOPER'}`, descX + 22, startY + 7.5);
        doc.text(`${selectedBike.model_year || '2026'}`, descX + 22, startY + 14.5);
        doc.text(`${selectedBike.engine_number || 'N/A'}`, descX + 22, startY + 21.5);
        doc.text(`${selectedBike.chassis_number || 'N/A'}`, descX + 22, startY + 28.5);

        // Values
        let valX = startX + colWidths[0] + colWidths[1];
        doc.text(`Rs. ${valueExSt.toFixed(2)}`, valX + (colWidths[2] / 2), startY + 17, { align: 'center' });
        doc.text(`${taxPercent}%`, valX + colWidths[2] + (colWidths[3] / 2), startY + 17, { align: 'center' });
        doc.text(`Rs. ${salesTaxAmount.toFixed(2)}`, valX + colWidths[2] + colWidths[3] + (colWidths[4] / 2), startY + 17, { align: 'center' });
        
        doc.setFont("helvetica", "bold");
        doc.text(`Rs. ${valueIncSt.toFixed(2)}`, valX + colWidths[2] + colWidths[3] + colWidths[4] + (colWidths[5] / 2), startY + 17, { align: 'center' });

        // Total Row
        startY += rowHeight;
        doc.setFillColor(241, 245, 249);
        doc.rect(startX, startY, 182, 8.5, 'F');
        doc.rect(startX, startY, 182, 8.5, 'S');
        doc.setFont("helvetica", "bold");
        doc.text('Total:', startX + colWidths[0] + 4, startY + 5.5);
        doc.text(`Rs. ${valueExSt.toFixed(2)}`, valX + (colWidths[2] / 2), startY + 5.5, { align: 'center' });
        doc.text(`Rs. ${salesTaxAmount.toFixed(2)}`, valX + colWidths[2] + colWidths[3] + (colWidths[4] / 2), startY + 5.5, { align: 'center' });
        doc.text(`Rs. ${valueIncSt.toFixed(2)}`, valX + colWidths[2] + colWidths[3] + colWidths[4] + (colWidths[5] / 2), startY + 5.5, { align: 'center' });

        // Footer Signatures
        startY += 26;
        doc.setFont("helvetica", "normal");
        doc.text('Sincerely,', 15, startY);
        doc.setFont("helvetica", "bold");
        doc.text('Authorized Signatory', 15, startY + 14);
        doc.text(selectedCompany.name.toUpperCase(), 15, startY + 20);

        // Footer Logo Handling
        if (selectedCompany.footer_logo) {
            const footerUrl = `${serverRoot}/uploads/${selectedCompany.footer_logo}`;
            const footerImgData = await loadImageAsBase64(footerUrl);
            if (footerImgData) {
                doc.addImage(footerImgData, 'PNG', 15, 260, 180, 18);
            }
        }

        doc.save(`Sales_Tax_Invoice_${selectedBike.chassis_number}.pdf`);
    };

    // Download Excel matching ST-Memon layout
    const downloadExcel = () => {
        if (!selectedCompany || !selectedBike) {
            alert('Select company and bike first!');
            return;
        }
        const formattedDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');
        const compNtn = selectedCompany.ntn || selectedCompany.NTN || selectedCompany.stn || '';
        const compStrn = selectedCompany.strn || selectedCompany.STRN || '';

        const data = [
            ["", "SALES TAX INVOICE"],
            [],
            ["", "", "Date", formattedDate, "", "", "", "", "Inv No.", "STI-001"],
            [],
            ["", "", "Name", selectedCompany.name],
            ["", "", "NTN No:", compNtn],
            ["", "", "STRN:", compStrn],
            ["", "", "Address:", selectedCompany.address || ''],
            [],
            ["", "", "Buyer Name:", buyerName],
            ["", "", "CNIC:", buyerCnic],
            [],
            [],
            ["", "", "Qty", "Description", "", "", "Value Ex ST", "Rate of Sales Tax", "Sales Tax"],
            [],
            ["", "", "", "Make", ":", selectedCompany.name || 'CHOPER'],
            [],
            ["", "", "", "Model", ":", selectedBike.model_year || '2026'],
            ["", "", 1, "", "", "", `=G19/(1+H19)`, `${taxPercent}%`, `=ROUND(G19*H19,0)`],
            ["", "", "", "Engine No.", ":", selectedBike.engine_number],
            [],
            ["", "", "", "Chassis No.", ":", selectedBike.chassis_number],
            [],
            ["", "", "Total", "", "", "", `=SUM(G15:G23)`, "", `=SUM(I15:I23)`]
        ];

        const ws = XLSX.utils.aoa_to_sheet(data);
        ws['!cols'] = [{ wch: 5 }, { wch: 5 }, { wch: 15 }, { wch: 25 }, { wch: 5 }, { wch: 25 }, { wch: 18 }, { wch: 18 }, { wch: 18 }];

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Sales Invoice");
        XLSX.writeFile(wb, `Sales_Tax_Invoice_${selectedBike.chassis_number}.xlsx`);
    };

    const formattedDisplayDate = new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-');

    const previewNtn = selectedCompany?.ntn || selectedCompany?.NTN || selectedCompany?.stn || 'N/A';
    const previewStrn = selectedCompany?.strn || selectedCompany?.STRN || 'N/A';

    return (
        <div style={styles.card}>
            <h3 style={styles.title}>📄 Generate Sales Tax Invoice (ST-Memon Format)</h3>

            {/* Selection Form */}
            <div style={styles.grid}>
                <div>
                    <label style={styles.label}>1. Select Company</label>
                    <select value={selectedCompanyId} onChange={(e) => setSelectedCompanyId(e.target.value)} style={styles.input}>
                        <option value="">-- Choose Company --</option>
                        {companies && companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                </div>

                <div style={{ position: 'relative' }}>
                    <label style={styles.label}>2. Search Bike (Engine/Chassis #)</label>
                    <input 
                        type="text" 
                        placeholder="Type Engine or Chassis number..." 
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        style={styles.input}
                    />
                    {searchResults.length > 0 && (
                        <div style={styles.dropdown}>
                            {searchResults.map(b => (
                                <div key={b.id} style={styles.dropdownItem} onClick={() => selectBike(b)}>
                                    <strong>{b.model_name}</strong> - Eng: {b.engine_number} | Chassis: {b.chassis_number}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {selectedBike && (
                <div style={styles.selectedBox}>
                    <strong>Selected Bike:</strong> {selectedBike.model_name} | Engine: {selectedBike.engine_number} | Chassis: {selectedBike.chassis_number}
                </div>
            )}

            {/* Invoice Details Inputs */}
            <div style={{ ...styles.grid, marginTop: '15px' }}>
                <div>
                    <label style={styles.label}>Buyer Name (Manual)</label>
                    <input 
                        type="text" 
                        value={buyerName} 
                        onChange={(e) => setBuyerName(e.target.value)} 
                        placeholder="Enter Buyer Name..." 
                        style={styles.input} 
                    />
                </div>
                <div>
                    <label style={styles.label}>CNIC Number (Manual / Auto-Dash)</label>
                    <input 
                        type="text" 
                        value={buyerCnic} 
                        onChange={handleCnicChange} 
                        placeholder="32203-5843348-9" 
                        style={styles.input} 
                    />
                </div>
                <div>
                    <label style={styles.label}>Total Value (Inc. S.T)</label>
                    <input type="number" value={totalValue} onChange={(e) => setTotalValue(e.target.value)} style={styles.input} placeholder="e.g. 435000" />
                </div>
                <div>
                    <label style={styles.label}>Sales Tax %</label>
                    <input type="number" value={taxPercent} onChange={(e) => setTaxPercent(e.target.value)} style={styles.input} />
                </div>
            </div>

            {/* Preview Section */}
            {selectedCompany && selectedBike && (
                <div style={styles.previewContainer}>
                    {/* Header Logo Preview */}
                    {selectedCompany.header_logo && (
                        <div style={{ textAlign: 'center', marginBottom: '10px' }}>
                            <img src={`${serverRoot}/uploads/${selectedCompany.header_logo}`} alt="Header Logo" style={{ maxHeight: '60px', objectFit: 'contain' }} />
                        </div>
                    )}

                    <div style={styles.previewHeader}>
                        <h3 style={{ margin: '0 0 5px 0', textTransform: 'uppercase', color: '#0f172a' }}>SALES TAX INVOICE</h3>
                        <div style={{ textAlign: 'left', background: '#f8fafc', padding: '10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '13px' }}>
                            <div><strong>Seller Name:</strong> {selectedCompany.name}</div>
                            <div><strong>NTN:</strong> {previewNtn} &nbsp;|&nbsp; <strong>STRN:</strong> {previewStrn}</div>
                            <div><strong>Address:</strong> {selectedCompany.address || 'N/A'}</div>
                        </div>
                    </div>

                    <div style={{ marginBottom: '10px', fontSize: '13px', background: '#fff', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1' }}>
                        <strong>Buyer Name:</strong> {buyerName || '---'} &nbsp;|&nbsp; <strong>CNIC:</strong> {buyerCnic || '---'}
                    </div>

                    {/* Web Preview Table */}
                    <table style={styles.table}>
                        <thead>
                            <tr style={{ background: '#0f172a', color: '#fff' }}>
                                <th style={styles.thTd}>Qty</th>
                                <th style={{ ...styles.thTd, width: '45%' }}>Description</th>
                                <th style={styles.thTd}>Value Excl. S.T</th>
                                <th style={styles.thTd}>S.T Rate</th>
                                <th style={styles.thTd}>Sales Tax</th>
                                <th style={styles.thTd}>Total Incl. S.T</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ ...styles.thTdBody, verticalAlign: 'middle' }}>1</td>
                                <td style={{ ...styles.thTdBody, textAlign: 'left', paddingLeft: '15px' }}>
                                    <div><strong>Make:</strong> {selectedBike.model_name || 'CHOPER'}</div>
                                    <div><strong>Model:</strong> {selectedBike.model_year || '2026'}</div>
                                    <div><strong>Engine No:</strong> {selectedBike.engine_number || 'N/A'}</div>
                                    <div><strong>Chassis No:</strong> {selectedBike.chassis_number || 'N/A'}</div>
                                </td>
                                <td style={{ ...styles.thTdBody, verticalAlign: 'middle' }}>Rs. {valueExSt.toFixed(2)}</td>
                                <td style={{ ...styles.thTdBody, verticalAlign: 'middle' }}>{taxPercent}%</td>
                                <td style={{ ...styles.thTdBody, verticalAlign: 'middle' }}>Rs. {salesTaxAmount.toFixed(2)}</td>
                                <td style={{ ...styles.thTdBody, verticalAlign: 'middle' }}><strong>Rs. {valueIncSt.toFixed(2)}</strong></td>
                            </tr>
                            <tr style={{ background: '#f1f5f9', fontWeight: 'bold' }}>
                                <td colSpan="2" style={{ ...styles.thTdBody, textAlign: 'right', paddingRight: '15px' }}>Total:</td>
                                <td style={styles.thTdBody}>Rs. {valueExSt.toFixed(2)}</td>
                                <td style={styles.thTdBody}>-</td>
                                <td style={styles.thTdBody}>Rs. {salesTaxAmount.toFixed(2)}</td>
                                <td style={styles.thTdBody}>Rs. {valueIncSt.toFixed(2)}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Footer Logo Preview */}
                    {selectedCompany.footer_logo && (
                        <div style={{ textAlign: 'center', marginTop: '20px' }}>
                            <img src={`${serverRoot}/uploads/${selectedCompany.footer_logo}`} alt="Footer Logo" style={{ maxHeight: '45px', objectFit: 'contain' }} />
                        </div>
                    )}
                </div>
            )}

            {/* Actions */}
            <div style={styles.actionRow}>
                <button onClick={handleSaveInvoice} style={styles.saveBtn}>💾 Save to Database</button>
                <button onClick={downloadPDF} style={styles.pdfBtn}>📄 Download PDF</button>
                <button onClick={downloadExcel} style={styles.excelBtn}>📊 Download Excel</button>
            </div>
        </div>
    );
};

const styles = {
    card: { background: '#fff', padding: '20px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' },
    title: { margin: '0 0 15px 0', fontSize: '18px', color: '#0f172a' },
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '15px' },
    label: { display: 'block', fontSize: '13px', fontWeight: '600', marginBottom: '5px', color: '#475569' },
    input: { width: '100%', padding: '10px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '14px', outline: 'none' },
    dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #cbd5e1', borderRadius: '6px', zIndex: 10, maxHeight: '150px', overflowY: 'auto', boxShadow: '0 4px 10px rgba(0,0,0,0.1)' },
    dropdownItem: { padding: '10px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', fontSize: '13px' },
    selectedBox: { background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '10px', borderRadius: '6px', marginTop: '15px', color: '#166534', fontSize: '13px' },
    previewContainer: { marginTop: '20px', border: '1px solid #e2e8f0', padding: '20px', borderRadius: '8px', background: '#fafafa' },
    previewHeader: { textAlign: 'center', marginBottom: '15px', borderBottom: '1px solid #e2e8f0', paddingBottom: '10px' },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '10px', background: '#fff' },
    thTd: { border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '12px' },
    thTdBody: { border: '1px solid #cbd5e1', padding: '8px', textAlign: 'center', fontSize: '12px', color: '#0f172a' },
    actionRow: { display: 'flex', gap: '10px', marginTop: '20px' },
    saveBtn: { background: '#0f172a', color: '#fff', padding: '10px 18px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' },
    pdfBtn: { background: '#ef4444', color: '#fff', padding: '10px 18px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' },
    excelBtn: { background: '#10b981', color: '#fff', padding: '10px 18px', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }
};

export default InvoiceGenerate;